/**
 * Assignee (Workspace v3.x design-system) — the single source for how an assignment
 * renders. <AssigneeAvatar> is the bubble (sage fill + initials when assigned, dashed
 * '?' when not, via the shared `avatarClasses`); <Assignee> is avatar + name, the
 * unified "Owner" chip (founder soak: intake ownership never reads as CM
 * assignment, so the label is "Owner", not "Assigned to"). Replaces the three
 * near-duplicates (the drawer's plain owner text, the card owner avatar, the
 * agenda owner avatar) so assignment looks identical everywhere. The
 * collaborator chip uses the same bubble.
 *
 * Handoff discoverability fix: the bubble stays a plain, aria-hidden span by
 * default, but becomes a real button once a caller passes `onClick` — the
 * WorkItemDrawer Owner row wires this so the avatar opens the hand-off picker,
 * matching the board card's owner avatar, which has always been a button. The
 * hover ring reuses the sage-400 token every focus ring in the workspace
 * already carries, so hover and focus read as the same affordance.
 */

import { type ReactNode } from 'react';
import { avatarClasses, initials } from './workspaceFormat';

const AVATAR_BUTTON_CLASSES =
  ' transition-shadow duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
  'hover:ring-2 hover:ring-sage-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export function AssigneeAvatar({
  name,
  size = 'md',
  className,
  onClick,
  title,
  ariaLabel,
  testId,
}: {
  name: string | null | undefined;
  size?: 'md' | 'sm';
  className?: string;
  /** Given only where the bubble IS a control (the drawer's Owner row); opens the hand-off picker. */
  onClick?: () => void;
  title?: string;
  ariaLabel?: string;
  testId?: string;
}): ReactNode {
  const assigned = !!name;
  const shape = avatarClasses(assigned, size) + (className ? ' ' + className : '');
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={ariaLabel ?? title}
        data-testid={testId}
        className={shape + AVATAR_BUTTON_CLASSES}
      >
        {assigned ? initials(name) : '?'}
      </button>
    );
  }
  return (
    <span className={shape} aria-hidden="true" data-testid={testId}>
      {assigned ? initials(name) : '?'}
    </span>
  );
}

export function Assignee({
  name,
  size = 'md',
  testId,
  onAvatarClick,
  avatarTitle,
  avatarAriaLabel,
  avatarTestId,
}: {
  name: string | null | undefined;
  size?: 'md' | 'sm';
  testId?: string;
  /** Passed through to the avatar bubble — see AssigneeAvatar's onClick. */
  onAvatarClick?: () => void;
  avatarTitle?: string;
  avatarAriaLabel?: string;
  avatarTestId?: string;
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0 align-middle" data-testid={testId}>
      <AssigneeAvatar
        name={name}
        size={size}
        onClick={onAvatarClick}
        title={avatarTitle}
        ariaLabel={avatarAriaLabel}
        testId={avatarTestId}
      />
      <span className={'truncate ' + (name ? 'text-ink' : 'text-sage-400')}>{name ?? 'Unassigned'}</span>
    </span>
  );
}
